/**
 * Stat Grid
 *
 * A responsive grid layout for displaying multiple stat cards.
 */

import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

interface StatGridProps {
  children: ReactNode;
  /** Number of columns on different breakpoints */
  columns?: 2 | 3 | 4;
  /** Gap between items */
  gap?: "sm" | "md" | "lg";
  /** Additional className */
  className?: string;
}

export function StatGrid({
  children,
  columns = 4,
  gap = "sm",
  className,
}: StatGridProps) {
  const columnClasses = {
    2: "grid-cols-2",
    3: "grid-cols-2 sm:grid-cols-3",
    4: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4",
  }[columns];

  const gapClasses = {
    sm: "gap-2",
    md: "gap-3",
    lg: "gap-4",
  }[gap];

  return (
    <div className={cn("grid", columnClasses, gapClasses, className)}>
      {children}
    </div>
  );
}

/**
 * Section wrapper for grouping related stats
 */
interface StatSectionProps {
  title?: string;
  children: ReactNode;
  className?: string;
}

export function StatSection({ title, children, className }: StatSectionProps) {
  return (
    <div className={cn("space-y-2", className)}>
      {title && (
        <h3 className="text-sm font-semibold text-muted-foreground">{title}</h3>
      )}
      {children}
    </div>
  );
}

